import { useEffect, useRef, useState } from "react";
import { ClipboardList, Loader2, ChevronLeft, ChevronRight, History as HistoryIcon, Printer, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { PageHeader } from "../../components/common/PageHeader";
import { currency, dateTime, todayISO } from "../../utils/format";
import { businessDateTimeValue } from "../../utils/sriLankaTime";
import { useAuth } from "../../store/AuthContext";
import { restaurantOrderApi } from "../../api/restaurantOrderApi";
import { toast } from "sonner";

const PAGE_SIZE = 3;

const newestPrintJob = (jobs = []) => [...jobs].sort((first, second) => {
  const createdOrder = businessDateTimeValue(second.createdAt) - businessDateTimeValue(first.createdAt);
  return createdOrder || Number(second.printJobId) - Number(first.printJobId);
})[0];

const CurrentDocumentAction = ({ documentType, document, busy, canRetry, canReprint, onAction }) => {
  if (document?.documentState !== "CURRENT") return null;

  const isRetry = document.latestPrintStatus === "FAILED";
  const isReprint = document.latestPrintStatus === "PRINTED";
  if ((isRetry && !canRetry) || (isReprint && !canReprint) || (!isRetry && !isReprint)) return null;
  return (
    <Button type="button" size="sm" variant="outline" disabled={busy}
      onClick={() => onAction(documentType, document, isRetry ? "RETRY" : "REPRINT")}
    >
      {busy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
        : isRetry ? <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
          : <Printer className="mr-1.5 h-3.5 w-3.5" />}
      {busy ? (isRetry ? "Retrying?" : "Reprinting?") : (isRetry ? "Retry" : "Reprint")}
    </Button>
  );
};

const getLocalDateString = () => todayISO();

const statusStyles = {
  CURRENT: "bg-emerald-100 text-emerald-800",
  PRINTED: "bg-emerald-100 text-emerald-800",
  COMPLETED: "bg-emerald-100 text-emerald-800",
  SUPERSEDED: "bg-slate-100 text-slate-600",
  CANCELLED: "bg-slate-100 text-slate-600",
  VOID: "bg-rose-100 text-rose-800",
  FAILED: "bg-rose-100 text-rose-800",
  PENDING: "bg-amber-100 text-amber-800",
  CORRECTION_REQUESTED: "bg-amber-100 text-amber-800",
  PRINTING: "bg-sky-100 text-sky-800",
};

const StateBadge = ({ value, fallback = "—" }) => {
  if (!value) {
    return <span className="text-sm text-slate-400">{fallback}</span>;
  }

  return (
    <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${statusStyles[value] || "bg-slate-100 text-slate-700"}`}>
      {value.replaceAll("_", " ")}
    </span>
  );
};

const OutletBadge = ({ value }) => (
  <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${value === "BAR" ? "bg-navy-100 text-navy-800" : "bg-orange-100 text-orange-800"}`}>
    {value || "—"}
  </span>
);

const SectionHeading = ({ children, trailing }) => (
  <div className="mb-3 flex items-center justify-between gap-3">
    <h3 className="font-display text-lg font-semibold text-slate-900">{children}</h3>
    {trailing}
  </div>
);

const ReceiptItems = ({ items = [] }) => (
  <div className="overflow-x-auto rounded-lg border border-slate-200">
    <table className="w-full min-w-[520px] text-sm">
      <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
        <tr>
          <th className="px-3 py-2">Item</th>
          <th className="px-3 py-2 text-right">Qty</th>
          <th className="px-3 py-2 text-right">Unit price</th>
          <th className="px-3 py-2 text-right">Line total</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100">
        {items.map((item, index) => (
          <tr key={`${item.itemName}-${index}`}>
            <td className="px-3 py-2 font-medium text-slate-800">{item.itemName}</td>
            <td className="px-3 py-2 text-right tabular-nums">{item.quantity}</td>
            <td className="px-3 py-2 text-right tabular-nums">{currency(item.unitPrice)}</td>
            <td className="px-3 py-2 text-right font-medium tabular-nums">{currency(item.lineTotal)}</td>
          </tr>
        ))}
        {items.length === 0 && (
          <tr><td colSpan={4} className="px-3 py-5 text-center text-slate-500">No Receipt items</td></tr>
        )}
      </tbody>
    </table>
  </div>
);

const KotItems = ({ items = [] }) => (
  <div className="overflow-hidden rounded-lg border border-slate-200">
    {items.length ? items.map((item, index) => (
      <div key={`${item.itemName}-${index}`} className="flex items-center justify-between gap-4 border-b border-slate-100 px-3 py-2.5 last:border-0">
        <span className="font-medium text-slate-800">{item.itemName}</span>
        <span className="rounded-md bg-slate-100 px-2 py-1 text-sm font-semibold tabular-nums">× {item.quantity}</span>
      </div>
    )) : <p className="px-3 py-5 text-center text-sm text-slate-500">No KOT items</p>}
  </div>
);

const PrintJobs = ({ jobs = [] }) => (
  <div className="mt-3 space-y-2">
    <h5 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Print history</h5>
    {jobs.length ? jobs.map((job) => (
      <div key={job.printJobId} className="rounded-lg bg-slate-50 px-3 py-2.5 text-sm">
        <div className="flex flex-wrap items-center gap-2">
          <StateBadge value={job.purpose} />
          <StateBadge value={job.status} />
          <span className="text-xs text-slate-500">Attempt {job.attemptCount}</span>
        </div>
        <div className="mt-2 grid gap-x-5 gap-y-1 text-xs text-slate-500 sm:grid-cols-2">
          <span>Created: {dateTime(job.createdAt)}</span>
          <span>Printed: {dateTime(job.printedAt)}</span>
        </div>
        {job.lastError && <p className="mt-2 break-words text-xs text-rose-700">{job.lastError}</p>}
      </div>
    )) : <p className="text-sm text-slate-500">No print attempts</p>}
  </div>
);

const ErrorMessage = ({ children }) => (
  <div role="alert" className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
    {children}
  </div>
);

const DocumentSummary = ({ label, document, numberLabel }) => (
  <div className="min-w-0 rounded-lg border border-slate-200 bg-white px-3 py-2.5">
    <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</div>
    {document ? (
      <div className="mt-1 flex flex-wrap items-center gap-2">
        <span className="font-semibold text-slate-900">{numberLabel}</span>
        <span className="text-xs text-slate-500">Rev {document.revision}</span>
        <StateBadge value={document.documentState} />
        <StateBadge value={document.latestPrintStatus} fallback="No print job" />
      </div>
    ) : <p className="mt-1 text-sm text-slate-400">Not created</p>}
  </div>
);

const HistoryDocuments = ({ history }) => (
  <div className="space-y-6">
    <section>
      <SectionHeading>Receipt revisions</SectionHeading>
      {history.receiptRevisions?.length ? (
        <div className="space-y-3">
          {history.receiptRevisions.map((receipt) => (
            <article key={receipt.receiptId} className={`rounded-xl border p-4 ${receipt.documentState === "CURRENT" ? "border-emerald-300 bg-emerald-50/40" : "border-slate-200 bg-white"}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex flex-wrap items-center gap-2">
                  <h4 className="font-semibold text-slate-900">{receipt.invoiceNumber}</h4>
                  <span className="text-sm text-slate-500">Revision {receipt.revision}</span>
                  <StateBadge value={receipt.documentState} />
                </div>
                <span className="text-xs text-slate-500">Created {dateTime(receipt.createdAt)}</span>
              </div>
              <div className="mt-3"><ReceiptItems items={receipt.items} /></div>
              <div className="mt-3 text-right text-sm font-semibold text-slate-800">Restaurant subtotal: {currency(receipt.restaurantItemSubtotal)}</div>
              <PrintJobs jobs={receipt.printJobs} />
            </article>
          ))}
        </div>
      ) : <p className="text-sm text-slate-500">No Receipt revisions</p>}
    </section>

    <section>
      <SectionHeading>KOT revisions</SectionHeading>
      {history.kotRevisions?.length ? (
        <div className="space-y-3">
          {history.kotRevisions.map((kot) => (
            <article key={kot.kotId} className={`rounded-xl border p-4 ${kot.documentState === "CURRENT" ? "border-emerald-300 bg-emerald-50/40" : "border-slate-200 bg-white"}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex flex-wrap items-center gap-2">
                  <h4 className="font-semibold text-slate-900">{kot.kotNumber}</h4>
                  <span className="text-sm text-slate-500">Revision {kot.revision}</span>
                  <StateBadge value={kot.documentState} />
                </div>
                <span className="text-xs text-slate-500">Created {dateTime(kot.createdAt)}</span>
              </div>
              <div className="mt-3"><KotItems items={kot.items} /></div>
              <PrintJobs jobs={kot.printJobs} />
            </article>
          ))}
        </div>
      ) : <p className="text-sm text-slate-500">No KOT revisions</p>}
    </section>
  </div>
);

export const RestaurantOrdersPage = () => {
  const { role } = useAuth();
  const canViewHistory = role === "ADMIN" || role === "OWNER";
  const canReprint = canViewHistory || role === "RES_CASHIER";
  const [initialDate] = useState(getLocalDateString);
  const [page, setPage] = useState(0);
  const [filterMode, setFilterMode] = useState("single");
  const [searchValue, setSearchValue] = useState("");
  const [businessDate, setBusinessDate] = useState(initialDate);
  const [fromDate, setFromDate] = useState(initialDate);
  const [toDate, setToDate] = useState(initialDate);
  const [appliedFilters, setAppliedFilters] = useState({
    fromDate: initialDate,
    toDate: initialDate,
  });
  const [filterError, setFilterError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  const [ordersPage, setOrdersPage] = useState(null);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState("");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState("");
  const [printActionKey, setPrintActionKey] = useState(null);
  const printActionInFlight = useRef(false);

  useEffect(() => {
    let active = true;
    setListLoading(true);
    setListError("");
    restaurantOrderApi.list({ page, size: PAGE_SIZE, ...appliedFilters })
      .then((result) => {
        if (active) setOrdersPage(result);
      })
      .catch((error) => {
        if (active) setListError(error?.response?.data?.message || "Could not load Restaurant Orders.");
      })
      .finally(() => {
        if (active) setListLoading(false);
      });
    return () => { active = false; };
  }, [page, appliedFilters, reloadKey]);

  useEffect(() => {
    const normalizedSearch = searchValue.trim();
    const appliedSearch = (appliedFilters.search || "").trim();
    if (normalizedSearch === appliedSearch) return undefined;

    const timeoutId = window.setTimeout(() => {
      setAppliedFilters((current) => ({ ...current, search: normalizedSearch }));
      setPage(0);
    }, 400);

    return () => window.clearTimeout(timeoutId);
  }, [searchValue, appliedFilters.search]);

  useEffect(() => {
    if (!selectedOrder) return undefined;
    let active = true;
    setDetailLoading(true);
    setDetailError("");
    restaurantOrderApi.detail(selectedOrder.restaurantOrderId)
      .then((result) => {
        if (active) setDetail(result);
      })
      .catch((error) => {
        if (active) setDetailError(error?.response?.data?.message || "Could not load order details.");
      })
      .finally(() => {
        if (active) setDetailLoading(false);
      });
    return () => { active = false; };
  }, [selectedOrder]);

  useEffect(() => {
    if (!historyOpen || !canViewHistory || !selectedOrder || history) return undefined;
    let active = true;
    setHistoryLoading(true);
    setHistoryError("");
    restaurantOrderApi.history(selectedOrder.restaurantOrderId)
      .then((result) => {
        if (active) setHistory(result);
      })
      .catch((error) => {
        if (active) setHistoryError(error?.response?.data?.message || "Could not load order history.");
      })
      .finally(() => {
        if (active) setHistoryLoading(false);
      });
    return () => { active = false; };
  }, [historyOpen, canViewHistory, selectedOrder, history]);

  const handlePrintAction = async (documentType, document, action) => {
    if (document.documentState !== "CURRENT") return;
    if (action === "RETRY" && (!canViewHistory || document.latestPrintStatus !== "FAILED")) return;
    if (action === "REPRINT" && (!canReprint || document.latestPrintStatus !== "PRINTED")) return;
    if (printActionInFlight.current) return;

    const documentId = documentType === "RECEIPT" ? document.receiptId : document.kotId;
    const actionKey = `${documentType}:${documentId}:${action}`;
    printActionInFlight.current = true;
    setPrintActionKey(actionKey);

    try {
      if (action === "RETRY") {
        const orderHistory = await restaurantOrderApi.history(detail.restaurantOrderId);
        const revisions = documentType === "RECEIPT"
          ? orderHistory.receiptRevisions
          : orderHistory.kotRevisions;
        const idField = documentType === "RECEIPT" ? "receiptId" : "kotId";
        const currentDocument = (revisions || []).find((revision) =>
          revision[idField] === documentId && revision.documentState === "CURRENT"
        );
        const latestJob = newestPrintJob(currentDocument?.printJobs || []);

        if (!latestJob || latestJob.status !== "FAILED") {
          toast.error("The latest print job is not FAILED. No retry was submitted.");
          return;
        }
        await restaurantOrderApi.retryPrintJob(latestJob.printJobId);
      } else {
        await restaurantOrderApi.reprintDocument(documentType, documentId);
      }

      toast.success(action === "RETRY" ? "Print job queued for retry" : "Reprint job queued");

      const refreshHistory = historyOpen;
      if (refreshHistory) setHistoryLoading(true);
      const refreshResults = await Promise.allSettled([
        restaurantOrderApi.detail(detail.restaurantOrderId),
        refreshHistory ? restaurantOrderApi.history(detail.restaurantOrderId) : Promise.resolve(null),
      ]);

      if (refreshResults[0].status === "fulfilled") {
        setDetail(refreshResults[0].value);
      } else {
        toast.error("Print action succeeded, but order details could not be refreshed.");
      }
      if (refreshHistory) {
        if (refreshResults[1].status === "fulfilled") {
          setHistory(refreshResults[1].value);
          setHistoryError("");
        } else {
          setHistoryError(refreshResults[1].reason?.response?.data?.message || "Could not refresh order history.");
        }
        setHistoryLoading(false);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not update the print job.");
    } finally {
      printActionInFlight.current = false;
      setPrintActionKey(null);
      setHistoryLoading(false);
    }
  };

  const searchOrders = () => {
    let nextFilters;
    if (filterMode === "single") {
      if (!businessDate) {
        setFilterError("Select a date before searching.");
        return;
      }
      nextFilters = { fromDate: businessDate, toDate: businessDate };
    } else {
      if (!fromDate || !toDate) {
        setFilterError("Select both a From Date and a To Date.");
        return;
      }
      if (fromDate > toDate) {
        setFilterError("From Date cannot be after To Date.");
        return;
      }
      nextFilters = { fromDate, toDate };
    }

    setFilterError("");
    setAppliedFilters({ ...nextFilters, search: searchValue.trim() });
    setPage(0);
    setReloadKey((current) => current + 1);
  };

  const resetFilters = () => {
    const today = getLocalDateString();
    setSearchValue("");
    setFilterMode("single");
    setBusinessDate(today);
    setFromDate(today);
    setToDate(today);
    setFilterError("");
    setAppliedFilters({ fromDate: today, toDate: today, search: "" });
    setPage(0);
    setReloadKey((current) => current + 1);
  };

  const closeDetail = (open) => {
    if (open) return;
    setSelectedOrder(null);
    setDetail(null);
    setHistory(null);
    setHistoryOpen(false);
    setHistoryError("");
  };

  const orders = Array.isArray(ordersPage?.content) ? ordersPage.content : [];
  const totalPages = Math.max(1, Number(ordersPage?.totalPages || 1));

  return (
    <div>
      <PageHeader
        title="Restaurant Orders"
        subtitle="Restaurant items sold through BAR and Restaurant POS"
        actions={<div className="flex items-center gap-2 text-sm text-slate-500"><ClipboardList className="h-4 w-4" />{ordersPage?.totalElements ?? 0} orders</div>}
      />

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div>
          <label htmlFor="restaurant-orders-search" className="mb-1 block text-xs font-medium text-slate-500">Search</label>
          <input
            id="restaurant-orders-search"
            type="search"
            value={searchValue}
            onChange={(event) => setSearchValue(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") searchOrders();
            }}
            placeholder="Search order / bill / cashier"
            className="w-64 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm shadow-sm"
            data-testid="restaurant-orders-search"
          />
        </div>
        <div className="flex rounded-xl bg-slate-100 p-1">
          <button
            type="button"
            onClick={() => setFilterMode("single")}
            className={`rounded-lg px-4 py-2 text-sm ${filterMode === "single" ? "bg-white text-navy-800 shadow-sm" : "text-slate-600"}`}
          >
            Single Date
          </button>
          <button
            type="button"
            onClick={() => setFilterMode("range")}
            className={`rounded-lg px-4 py-2 text-sm ${filterMode === "range" ? "bg-white text-navy-800 shadow-sm" : "text-slate-600"}`}
          >
            Date Range
          </button>
        </div>

        {filterMode === "single" ? (
          <div>
            <label htmlFor="restaurant-order-date" className="mb-1 block text-xs font-medium text-slate-500">Date</label>
            <input
              id="restaurant-order-date"
              type="date"
              value={businessDate}
              onChange={(event) => setBusinessDate(event.target.value)}
              className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm shadow-sm"
            />
          </div>
        ) : (
          <>
            <div>
              <label htmlFor="restaurant-orders-from-date" className="mb-1 block text-xs font-medium text-slate-500">From Date</label>
              <input
                id="restaurant-orders-from-date"
                type="date"
                value={fromDate}
                onChange={(event) => setFromDate(event.target.value)}
                className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm shadow-sm"
              />
            </div>
            <div>
              <label htmlFor="restaurant-orders-to-date" className="mb-1 block text-xs font-medium text-slate-500">To Date</label>
              <input
                id="restaurant-orders-to-date"
                type="date"
                value={toDate}
                onChange={(event) => setToDate(event.target.value)}
                className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm shadow-sm"
              />
            </div>
          </>
        )}

        <div className="flex gap-2">
          <button
            type="button"
            onClick={searchOrders}
            className="inline-flex items-center gap-2 rounded-xl bg-navy-700 px-5 py-2.5 text-sm text-white hover:bg-navy-800"
          >
            Search
          </button>
          <button
            type="button"
            onClick={resetFilters}
            className="rounded-xl border border-slate-200 bg-white px-5 py-2.5 text-sm text-slate-700 shadow-sm hover:bg-slate-50"
          >
            Reset
          </button>
        </div>
      </div>
      {filterError && <div className="mb-4"><ErrorMessage>{filterError}</ErrorMessage></div>}

      <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="hidden grid-cols-[minmax(150px,1.2fr)_minmax(110px,.8fr)_minmax(140px,1fr)_minmax(140px,1fr)_minmax(210px,1.4fr)_minmax(250px,1.7fr)] gap-4 border-b border-slate-200 bg-slate-50 px-5 py-3 text-[11px] font-semibold uppercase tracking-wide text-slate-500 xl:grid">
          <span>Order</span><span>Outlet</span><span>Sale bill</span><span>Cashier</span><span>Date / state</span><span>Documents</span>
        </div>

        {listLoading ? (
          <div className="flex items-center justify-center gap-2 px-4 py-14 text-sm text-slate-500"><Loader2 className="h-5 w-5 animate-spin" />Loading orders…</div>
        ) : listError ? (
          <div className="p-5"><ErrorMessage>{listError}</ErrorMessage></div>
        ) : orders.length === 0 ? (
          <div className="px-4 py-14 text-center text-sm text-slate-500">No Restaurant Orders found.</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {orders.map((order) => (
              <button
                key={order.restaurantOrderId}
                type="button"
                onClick={() => {
                  setSelectedOrder(order);
                  setDetail(null);
                  setHistory(null);
                  setHistoryError("");
                  setHistoryOpen(false);
                }}
                className="grid w-full grid-cols-1 gap-3 px-4 py-4 text-left transition hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-navy-500 sm:px-5 xl:grid-cols-[minmax(150px,1.2fr)_minmax(110px,.8fr)_minmax(140px,1fr)_minmax(140px,1fr)_minmax(210px,1.4fr)_minmax(250px,1.7fr)] xl:items-center xl:gap-4"
              >
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">Order Number</div>
                  <div className="mt-0.5 font-display text-xl font-bold text-navy-900">{order.orderNumber}</div>
                </div>
                <div><div className="mb-1 text-[11px] font-semibold uppercase text-slate-400 xl:hidden">Outlet</div><OutletBadge value={order.sourceOutletCode} /></div>
                <div className="text-sm font-medium text-slate-800"><span className="xl:hidden text-[11px] font-semibold uppercase text-slate-400">Sale bill: </span>{order.saleBillNumber || "—"}</div>
                <div className="text-sm text-slate-700"><span className="xl:hidden text-[11px] font-semibold uppercase text-slate-400">Cashier: </span>{order.cashierName || "—"}</div>
                <div className="flex flex-wrap items-center gap-2 xl:block">
                  <div className="text-sm text-slate-700">{dateTime(order.saleDateTime)}</div>
                  <div className="mt-1"><StateBadge value={order.orderState} /></div>
                </div>
                <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-1">
                  <DocumentSummary label="Receipt" document={order.currentReceipt} numberLabel={order.currentReceipt?.number} />
                  <DocumentSummary label="KOT" document={order.currentKot} numberLabel={order.currentKot?.number} />
                </div>
              </button>
            ))}
          </div>
        )}

        <div className="flex flex-col gap-3 border-t border-slate-200 bg-slate-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-5">
          <span className="text-sm text-slate-500">Page {Math.min(page + 1, totalPages)} of {totalPages}</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 0 || listLoading} onClick={() => setPage((current) => Math.max(0, current - 1))}>
              <ChevronLeft className="mr-1 h-4 w-4" />Previous
            </Button>
            <Button variant="outline" size="sm" disabled={page + 1 >= totalPages || listLoading} onClick={() => setPage((current) => current + 1)}>
              Next<ChevronRight className="ml-1 h-4 w-4" />
            </Button>
          </div>
        </div>
      </section>

      <Dialog open={!!selectedOrder} onOpenChange={closeDetail}>
        <DialogContent className="max-h-[92vh] w-[calc(100%-1rem)] max-w-5xl overflow-y-auto p-4 sm:p-6">
          <DialogHeader className="pr-8">
            <DialogTitle className="font-display text-2xl">Order {detail?.orderNumber || selectedOrder?.orderNumber}</DialogTitle>
            <DialogDescription>Restaurant order details and immutable document snapshots.</DialogDescription>
          </DialogHeader>

          {detailLoading ? (
            <div className="flex items-center justify-center gap-2 py-14 text-sm text-slate-500"><Loader2 className="h-5 w-5 animate-spin" />Loading order details…</div>
          ) : detailError ? <ErrorMessage>{detailError}</ErrorMessage> : detail ? (
            <div className="space-y-6">
              <section className="rounded-xl bg-slate-50 p-4">
                <SectionHeading>Order</SectionHeading>
                <div className="grid gap-3 text-sm sm:grid-cols-2 lg:grid-cols-3">
                  <div><span className="text-slate-500">Order Number</span><div className="font-display text-xl font-bold text-navy-900">{detail.orderNumber}</div></div>
                  <div><span className="text-slate-500">Source outlet</span><div className="mt-1"><OutletBadge value={detail.sourceOutletCode} /></div></div>
                  <div><span className="text-slate-500">Sale bill</span><div className="font-medium text-slate-900">{detail.saleBillNumber || "—"}</div></div>
                  <div><span className="text-slate-500">Cashier</span><div className="font-medium text-slate-900">{detail.cashierName || "—"}</div><div className="text-xs text-slate-500">ID {detail.cashierId ?? "—"}</div></div>
                  <div><span className="text-slate-500">Sale date/time</span><div className="font-medium text-slate-900">{dateTime(detail.saleDateTime)}</div></div>
                  <div><span className="text-slate-500">Order state</span><div className="mt-1"><StateBadge value={detail.orderState} /></div></div>
                </div>
              </section>

              <section>
                <SectionHeading trailing={detail.receipt && <div className="flex flex-wrap items-center gap-2"><StateBadge value={detail.receipt.documentState} /><StateBadge value={detail.receipt.latestPrintStatus} fallback="No print job" />{canReprint && <CurrentDocumentAction documentType="RECEIPT" document={detail.receipt} busy={Boolean(printActionKey)} canRetry={canViewHistory} canReprint={canReprint} onAction={handlePrintAction} />}</div>}>
                  Receipt {detail.receipt?.invoiceNumber ? <span className="text-slate-500">· {detail.receipt.invoiceNumber} · Rev {detail.receipt.revision}</span> : null}
                </SectionHeading>
                {detail.receipt ? <>
                  <ReceiptItems items={detail.receipt.items} />
                  <div className="mt-3 text-right font-semibold text-slate-900">Restaurant subtotal: {currency(detail.receipt.restaurantItemSubtotal)}</div>
                </> : <p className="rounded-lg bg-slate-50 px-4 py-5 text-sm text-slate-500">No current Receipt.</p>}
              </section>

              <section>
                <SectionHeading trailing={detail.kot && <div className="flex flex-wrap items-center gap-2"><StateBadge value={detail.kot.documentState} /><StateBadge value={detail.kot.latestPrintStatus} fallback="No print job" />{canReprint && <CurrentDocumentAction documentType="KOT" document={detail.kot} busy={Boolean(printActionKey)} canRetry={canViewHistory} canReprint={canReprint} onAction={handlePrintAction} />}</div>}>
                  KOT {detail.kot?.kotNumber ? <span className="text-slate-500">· {detail.kot.kotNumber} · Rev {detail.kot.revision}</span> : null}
                </SectionHeading>
                {detail.kot ? <KotItems items={detail.kot.items} /> : <p className="rounded-lg bg-slate-50 px-4 py-5 text-sm text-slate-500">No KOT for this order.</p>}
              </section>

              {canViewHistory && (
                <section className="border-t border-slate-200 pt-4">
                  <Button type="button" variant="outline" onClick={() => setHistoryOpen((open) => !open)} aria-expanded={historyOpen}>
                    <HistoryIcon className="mr-2 h-4 w-4" />{historyOpen ? "Hide document history" : "View document history"}
                  </Button>
                  {historyOpen && <div className="mt-4">
                    {historyLoading ? <div className="flex items-center gap-2 py-8 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" />Loading document history…</div>
                      : historyError ? <ErrorMessage>{historyError}</ErrorMessage>
                        : history ? <HistoryDocuments history={history} /> : null}
                  </div>}
                </section>
              )}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
};
